import { InfoWindow, Marker as MapMarker } from "@react-google-maps/api";
import { OpeningsSpecialDates, OpenToday } from "@Tmp/Openings";
import * as React from "react";
import { FindDealerMarkerProps } from ".";
import DealerIcons from "./DealerIcons";

/*
const icons = {
    selected:
        "https://mt.google.com/vt/icon?psize=30&font=fonts/arialuni_t.ttf&color=ff304C13&name=icons/spotlight/spotlight-waypoint-a.png&ax=43&ay=48&text=%E2%80%A2",
    nearby: "https://mt.google.com/vt/icon?color=ff004C13&name=icons/spotlight/spotlight-waypoint-blue.png",
    default: "https://mt.googleapis.com/vt/icon/name=icons/spotlight/spotlight-poi.png&scale=1",
};*/

const icons = {
    default: "https://resources.tmp.dk/images/icons/markers/motorcycle-default.svg",
    nearby: "https://resources.tmp.dk/images/icons/markers/motorcycle-near.svg",
    selected: "https://resources.tmp.dk/images/icons/markers/motorcycle-selected.svg",
    workshop: {
        default: "https://resources.tmp.dk/images/icons/markers/workshop-default.svg",
        nearby: "https://resources.tmp.dk/images/icons/markers/workshop-near.svg",
        selected: "https://resources.tmp.dk/images/icons/markers/workshop-selected.svg",
    },
};

export default function Marker({
    index,
    currentLocation,
    listElement,
    mapOptions,
    dealer,
    activeDealer,
    setActiveDealer,
    selectedDealer,
    setSelectedDealer,
    setListOpen,
    ...props
}: FindDealerMarkerProps) {
    const [marker, setMarker] = React.useState(null);

    const setTooltipOpen = (value) => {
        setActiveDealer(value ? dealer.id : null);
    };

    const handleMarkerClick = () => {
        scrollToDealerOnList();
    };

    const handleSeeListClick = () => {
        setListOpen(true);

        setTimeout(() => {
            scrollToDealerOnList();
        }, 200);
    };

    const scrollToDealerOnList = () => {
        const el = jQuery(dealer?.element?.current);
        let listEl = jQuery(listElement);

        if (!listEl?.length) {
            return;
        }

        listEl.stop().animate(
            {
                scrollTop:
                    el.offset().top -
                    el.parent().offset().top -
                    (listEl.height() / 2 - el.height() / 2),
            },
            750
        );
    };

    const tooltipOpen = activeDealer === dealer.id;
    const isNear = currentLocation ? index <= mapOptions.nearestDealersCount - 1 : false;

    const getSelectedIcon = () => {
        if (dealer.is_workshop) {
            const setting = props.settings?.find_dealer_workshop_marker_selected_icon?.url;
            return setting || icons.workshop.selected;
        }

        const setting = props.settings?.find_dealer_marker_selected_icon?.url;
        return setting || icons.selected;
    };

    const getNearbyIcon = () => {
        if (dealer.is_workshop) {
            const setting = props.settings?.find_dealer_workshop_marker_nearby_icon?.url;
            return setting || icons.workshop.nearby;
        }

        const setting = props.settings?.find_dealer_marker_nearby_icon?.url;
        return setting || icons.nearby;
    };

    const getDefaultIcon = () => {
        if (dealer.is_workshop) {
            const setting = props.settings?.find_dealer_workshop_marker_default_icon?.url;
            return setting || icons.workshop.default;
        }

        const setting = props.settings?.find_dealer_marker_default_icon?.url;
        return setting || icons.default;
    };

    const getIcon = () => {
        if (selectedDealer === dealer.id) {
            return getSelectedIcon();
        }

        if (isNear) {
            return getNearbyIcon();
        }

        return getDefaultIcon();
    };

    const handleLoad = React.useCallback((marker: google.maps.Marker) => {
        setMarker(marker);
    }, []);

    const handleUnmount = React.useCallback((marker: google.maps.Marker) => {
        setMarker(null);
    }, []);

    if (
        typeof dealer.address.coordinates.lat !== "number" ||
        typeof dealer.address.coordinates.lng !== "number"
    ) {
        return null;
    }

    return (
        <MapMarker
            position={dealer.address.coordinates}
            icon={getIcon()}
            onLoad={handleLoad}
            onUnmount={handleUnmount}
            onClick={() => {
                setTooltipOpen(!tooltipOpen);
                handleMarkerClick();
            }}
        >
            {marker && tooltipOpen && (
                <InfoWindow onCloseClick={() => setTooltipOpen(false)}>
                    <div className="tmp-find-dealer__marker__content">
                        <h4>{dealer.name}</h4>
                        <p>
                            {dealer.address.street + " " + dealer.address.house_number}
                            <br />
                            {dealer.address.zipcode + " " + dealer.address.city}
                        </p>
                        {dealer.distance && (
                            <p>
                                <span className="tmp-find-dealer__inline-heading">
                                    {props.settings.find_dealer_distance_title}
                                </span>
                                {(dealer.distance / 1000).toFixed(1)}
                                {props.settings.find_dealer_km_text}
                                <br />
                            </p>
                        )}
                        <p>
                            {dealer.phone && (
                                <>
                                    <span className="tmp-find-dealer__inline-heading">
                                        {props.settings.find_dealer_phone_title}
                                    </span>
                                    <a href={"tel:" + dealer.phone}>{dealer.phone}</a>
                                </>
                            )}
                            <br />
                            {dealer.email && (
                                <>
                                    <span className="tmp-find-dealer__inline-heading">
                                        {props.settings.find_dealer_email_title}
                                    </span>
                                    <a href={"mailto:" + dealer.email}>{dealer.email}</a>
                                </>
                            )}
                        </p>
                        <p>
                            <a
                                className="tmp-find-dealer__marker__open-list"
                                onClick={handleSeeListClick}
                            >
                                {props.settings.find_dealer_go_to_list_text}
                            </a>
                        </p>
                        {setSelectedDealer && (
                            <p>
                                {selectedDealer === dealer.id ? (
                                    <span className="tmp-find-dealer__select">
                                        {props.settings.find_dealer_long_selected_text}
                                    </span>
                                ) : (
                                    <span
                                        className="tmp-find-dealer__select cursor-pointer"
                                        onClick={() => {
                                            setSelectedDealer(dealer.id);
                                        }}
                                    >
                                        {props.settings.find_dealer_select_text}
                                    </span>
                                )}
                            </p>
                        )}
                        <DealerIcons settings={props.settings} dealer={dealer} />
                        <OpenToday openings={dealer.openings} />
                        <OpeningsSpecialDates dates={dealer.openings.special} />
                        {!!dealer &&
                            dealer?.url !== null &&
                            !!props.settings.find_dealer_link_text && (
                                <div style={{ marginTop: "0.9rem" }}>
                                    <a href={dealer.url}>{props.settings.find_dealer_link_text}</a>
                                </div>
                            )}
                    </div>
                </InfoWindow>
            )}
        </MapMarker>
    );
}
